import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import RenewableEnergyCertificateService from '../../../../shared/services/renewable-energy-certificate/renewable-energy-certificate.service';
import { useContext, useEffect, useState } from 'react';
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import { IPostRenewableEnergyCertificate, IReneweableEnergyCertificate } from '../../../../shared/services/renewable-energy-certificate/renewable-energy-certificate.model';
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading';
import { IconName } from '../../../../shared/interfaces/icon-picker.model';
import { a11yProps, deleteQueryParamFromUrl } from '../../../../shared/utils/misc-shared-utils';
import { Box, Tab, Tabs } from '@mui/material';
import { CustomTabPanel } from '../../../presentation-components/display-elements-components/custom-tab-panel';
import { LoadingPage } from '../../loading-page';
import { RenewableEnergyCertificateForm } from '../../../presentation-components/organisation-components/rec-form';
import { Emissions } from '../../../presentation-components/emissons-components/overview/emissions';

export const OrganisationRenewableEnergyCertificateEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams()
  //Services
  const renewableEnergyCertificateService = new RenewableEnergyCertificateService()
  const reportPeriodContext = useContext(ReportPeriodContext);
  const userNotificationsContext = useContext(UserNotificationsContext)
  //State
  const [value, setValue] = useState(0)
  const [renewableEnergyCertificate, setRenewableEnergyCertificate] = useState<IReneweableEnergyCertificate>();
  const [editField, setEditField] = useState<string>('')
  const [errorMessage, setErrorMessage] = useState<string>()
  const [errorCode, setErrorCode] = useState<number>()
  //Loading State
  const [isFormSubmitting, setIsFormSubitting] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  // Use Effects
  useEffect(() => {
    if (id && value === 0) {
      getRenewableEnergyCertificate(id)
    }
  }, [reportPeriodContext.lastSelectedReportId, id, value])

  useEffect(() => {
    if (searchParams.has('tabId')) {
      const tab = searchParams.get('tabId')
      if (tab) {
        const tabIndex = parseInt(tab)
        setValue(tabIndex)
        if (tabIndex === 1) {
          setIsLoading(false)
        }
      }
      deleteQueryParamFromUrl(searchParams, 'tabId')
    }
  }, [searchParams])


  const getRenewableEnergyCertificate = (id: string) => {
    renewableEnergyCertificateService.getOne(id, `?reportPeriodId=${reportPeriodContext.lastSelectedReportId}`)
      .then(response => {
        setRenewableEnergyCertificate(response)
        setIsLoading(false)
      })
      .catch(err => {
        console.log(err)
        setIsLoading(false)
      })
  }

  const submitHandler = async (payload: { values: IPostRenewableEnergyCertificate, files: File[], removedResourceIds: string[] }) => {
    if (id && payload?.values) {
      if (!isFormSubmitting) {
        setIsFormSubitting(true)
        const form = new FormData();
        if (payload.files && payload.files.length > 0) {
          for (let i = 0; i < payload.files.length; i++) {
            form.append('files', payload.files[i]);
          }
        }
        form.append('renewableEnergyCertificate', JSON.stringify(payload.values));
        if (payload.removedResourceIds && payload.removedResourceIds.length > 0) {
          form.append('removedResourceIds', JSON.stringify(payload.removedResourceIds));
        }
        renewableEnergyCertificateService.update(id, form)
          .then(response => {
            getRenewableEnergyCertificate(response.id!)
            setEditField('')
            userNotificationsContext.openSnackbar({ open: true, message: 'Updated successfully' });
            setIsFormSubitting(false)
          })
          .catch(err => {
            if (err.response.data.message) {
              setErrorMessage(err.response.data.message)
              setErrorCode(err.response.data.code)
              userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
            } else {
              userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
            }
            setIsFormSubitting(false)
            console.log(err)
          })
      }
    }
  }

  const deleteHandler = (id: string) => {
    if (renewableEnergyCertificate?.recInUse) {
      userNotificationsContext.openSnackbar({ open: true, message: `This renewable energy certificate cannot be deleted because it is currently in use, or has been used previously` });
      return
    }
    userNotificationsContext.openDialog({ active: true, title: 'Confirmation', message: `Are you sure you want to delete this renewable energy certificate? This action cannot be undone.`, buttonCopy: 'Continue', cancelButtonCopy: 'Cancel' })
      .then(response => {
        if (response && !isFormSubmitting) {
          setIsFormSubitting(true)
          renewableEnergyCertificateService.delete(id)
            .then(() => {
              userNotificationsContext.openSnackbar({ open: true, message: 'Deleted successfully' });
              navigate('/organisation/renewable-energy-certificates')
              setIsFormSubitting(false)
            })
            .catch(err => {
              if (err.response?.data?.code === 1) {
                userNotificationsContext.openDialog({
                  active: true, title: 'Action Not Allowed',
                  message: `This renewable energy certificate cannot be deleted because it is currently in use, or has been used previously.`,
                  buttonCopy: 'Ok',
                  hideCancelButton: true
                })
              } else {
                userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
              }
              setIsFormSubitting(false)
              console.log(err)
            })
        }
      })
  }

  // Component Event Handlers
  const changeTabHandler = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    if (newValue === 0) {
      setIsLoading(true)
    } else {
      setIsLoading(false)
    }
  };

  return (
    <PageOuterContainer>
      {renewableEnergyCertificate &&
        <>
          <PageHeading
            title={`Certificate ${renewableEnergyCertificate.certificateNumber}`}
            includeBackButton={true}
            backButtonLink='/organisation/renewable-energy-certificates'
            backButtonText='Back to renewable energy certificates'
            hasTabs={true}
            includeActionButton={true}
            actionButtonIcon={IconName.DeleteOutline}
            actionButtonHandler={() => deleteHandler(renewableEnergyCertificate.id)}
            actionButtonText='Delete'
            disableActionButton={renewableEnergyCertificate.recInUse}
            showChip={renewableEnergyCertificate.retired || renewableEnergyCertificate.recInUse}
            chipColor={renewableEnergyCertificate.retired ? 'error' : renewableEnergyCertificate.recInUse ? 'warning' : undefined}
            chipLabel={renewableEnergyCertificate.retired ? 'Retired' : renewableEnergyCertificate.recInUse ? 'Partially Used' : undefined}
          />
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={value} onChange={changeTabHandler}>
              <Tab label='Details' {...a11yProps(0)} sx={{ borderRadius: 1 }} />
              <Tab label='Emissions' {...a11yProps(1)} sx={{ borderRadius: 1 }} />
            </Tabs>
          </Box>
          <CustomTabPanel value={value} index={0}>
            <LoadingPage pageReady={!isLoading}>
              <RenewableEnergyCertificateForm
                newRenewableEnergyCertificate={{
                  name: renewableEnergyCertificate.name,
                  notes: renewableEnergyCertificate.notes,
                  certificateNumber: renewableEnergyCertificate.certificateNumber,
                  registry: renewableEnergyCertificate.registry,
                  generationPeriod: renewableEnergyCertificate.generationPeriod || '',
                  energyMwh: renewableEnergyCertificate.energyMwh,
                  certificateType: renewableEnergyCertificate.certificateType || '',
                  source: renewableEnergyCertificate.source || '',
                  retired: renewableEnergyCertificate.retired,
                  retiredDate: renewableEnergyCertificate.retiredDate,
                  uploadedResources: renewableEnergyCertificate.uploadedResources,
                  sourceOther: renewableEnergyCertificate.sourceOther || '',
                }}
                energyKwhUsed={renewableEnergyCertificate.energykwhUsed}
                isEditPage={true}
                editField={editField}
                setEditField={setEditField}
                submitHandler={submitHandler}
                errorMessage={errorMessage}
                errorCode={errorCode}
                setErrorMessage={setErrorMessage}
                setErrorCode={setErrorCode}
                renewableEnergyCertificateActive={!renewableEnergyCertificate.recInUse}
              />
            </LoadingPage>
          </CustomTabPanel>
          <CustomTabPanel value={value} index={1}>
            <LoadingPage pageReady={!isLoading}>
              {reportPeriodContext.lastSelectedReportId && id && (
                <Emissions
                  includeDelete={false}
                  includeEdit={false}
                  queryString={`&renewableEnergyCertificateId=${id}&recOnly=true`}
                  backLink={`/organisation/renewable-energy-certificates/${id}?tabId=1`}
                  backText='Back to renewable energy certificate'
                />
              )}
            </LoadingPage>
          </CustomTabPanel>
        </>}
    </PageOuterContainer>
  )
}